export const TYPEWRITER_PHASE = {
  WAITING: "waiting",
  TYPING: "typing",
  HOLDING: "holding",
  DELETING: "deleting",
};

export const TYPEWRITER_START_DELAY_MS = 800;
export const TYPEWRITER_TYPE_MS = 85;
export const TYPEWRITER_TYPE_JITTER_MS = 40;
export const TYPEWRITER_HOLD_MS = 2400;
export const TYPEWRITER_DELETE_MS = 35;
export const TYPEWRITER_NEXT_PHRASE_DELAY_MS = 600;

export const TYPEWRITER_INITIAL_STATE = {
  phase: TYPEWRITER_PHASE.WAITING,
  phraseIndex: 0,
  charIndex: 0,
};

const getPhraseIndex = ({ phraseIndex, phraseCount }) => {
  if (phraseCount <= 0) {
    return 0;
  }
  return ((phraseIndex % phraseCount) + phraseCount) % phraseCount;
};

export const getNextTypewriterState = ({ state, phrases }) => {
  if (!phrases?.length) {
    return state;
  }

  switch (state.phase) {
    case TYPEWRITER_PHASE.WAITING:
      return {
        phase: TYPEWRITER_PHASE.TYPING,
        phraseIndex: getPhraseIndex({
          phraseIndex: state.phraseIndex,
          phraseCount: phrases.length,
        }),
        charIndex: 0,
      };
    case TYPEWRITER_PHASE.TYPING: {
      const phrase = phrases[state.phraseIndex] ?? "";
      if (state.charIndex < phrase.length) {
        return {
          ...state,
          charIndex: state.charIndex + 1,
        };
      }
      return {
        ...state,
        phase: TYPEWRITER_PHASE.HOLDING,
      };
    }
    case TYPEWRITER_PHASE.HOLDING:
      return {
        ...state,
        phase: TYPEWRITER_PHASE.DELETING,
      };
    case TYPEWRITER_PHASE.DELETING:
      if (state.charIndex > 0) {
        return {
          ...state,
          charIndex: state.charIndex - 1,
        };
      }
      return {
        phase: TYPEWRITER_PHASE.WAITING,
        phraseIndex: (state.phraseIndex + 1) % phrases.length,
        charIndex: 0,
      };
    default: {
      const unknownPhase = state.phase;
      throw new Error(`Unknown typewriter phase: ${unknownPhase}`);
    }
  }
};

export const getTypewriterDelayMs = ({
  state,
  isFirstRun = false,
  random = Math.random,
}) => {
  switch (state.phase) {
    case TYPEWRITER_PHASE.WAITING:
      return isFirstRun
        ? TYPEWRITER_START_DELAY_MS
        : TYPEWRITER_NEXT_PHRASE_DELAY_MS;
    case TYPEWRITER_PHASE.TYPING:
      return TYPEWRITER_TYPE_MS + random() * TYPEWRITER_TYPE_JITTER_MS;
    case TYPEWRITER_PHASE.HOLDING:
      return TYPEWRITER_HOLD_MS;
    case TYPEWRITER_PHASE.DELETING:
      return TYPEWRITER_DELETE_MS;
    default: {
      const unknownPhase = state.phase;
      throw new Error(`Unknown typewriter phase: ${unknownPhase}`);
    }
  }
};

export const getTypewriterText = ({ state, phrases }) => {
  if (!phrases?.length) {
    return "";
  }
  if (state.phase === TYPEWRITER_PHASE.WAITING) {
    return "";
  }

  const phraseIndex = getPhraseIndex({
    phraseIndex: state.phraseIndex,
    phraseCount: phrases.length,
  });
  const phrase = phrases[phraseIndex] ?? "";
  const charIndex = Math.min(Math.max(state.charIndex, 0), phrase.length);
  return phrase.slice(0, charIndex);
};
